import { Color } from '@coast/schemas';
import { isNil, isNumber, isString } from 'lodash';
import { CSSProperties, ReactNode } from 'react';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { LoadingIndicator } from '@shared/components/loading';
import { Colors, grid } from '@shared/styles';
import styles from './Pill.module.css';

export type PillColor = Color | 'white' | 'transparent' | 'backgroundGrayLight';

interface Props {
  children?: ReactNode;
  sections?: ReactNode[];
  noBorder?: boolean;
  backgroundColor?: PillColor;
  round?: boolean;
  maxWidth?: CSSProperties['maxWidth'];
  numberOfLines?: number;
  onRemove?(): void;
  italic?: boolean;
  noLeftMargin?: boolean;
  noRightMargin?: boolean;
  borderColor?: string;
  borderWidth?: number;
  style?: StyleProp<ViewStyle>;
  textStyle?: CSSProperties;
  onPress?: VoidFunction;
  loading?: boolean;
  strikeThrough?: boolean;
}

export function Pill({
  children,
  sections = [children],
  backgroundColor = 'backgroundGrayLight',
  round = false,
  noBorder = false,
  maxWidth = '256px',
  numberOfLines = 1,
  onRemove,
  italic,
  noLeftMargin = false,
  noRightMargin = false,
  borderColor,
  borderWidth,
  style,
  textStyle,
  onPress,
  loading = false,
  strikeThrough = false,
}: Readonly<Props>) {
  const isOneLine = numberOfLines === 1;
  const pressable = !loading && !isNil(onPress);

  return (
    <View
      className={[
        styles.pill,
        pressable && styles.pressable,
        italic && styles.italic,
        round && styles.round,
        noBorder && styles.noBorder,
        noLeftMargin && styles.noLeftMargin,
        noRightMargin && styles.noRightMargin,
        isOneLine && styles.oneLine,
      ]}
      style={{
        backgroundColor: Colors.v2[backgroundColor],
        ...(!isNil(borderColor) && { borderColor }),
        ...(!isNil(borderWidth) && { borderWidth }),
        ...convertPillStyleToCss(style),
      }}
      onClick={pressable ? onPress : undefined}
    >
      {loading && (
        <View className={styles.loading}>
          <LoadingIndicator />
        </View>
      )}
      {sections.map((section, index) => (
        <PillSection
          key={index}
          strikeThrough={strikeThrough}
          className={[styles.section, index > 0 && styles.notFirst, loading && styles.disabled]}
          maxWidth={maxWidth}
          italic={italic}
          numberOfLines={numberOfLines}
          textStyle={textStyle}
        >
          {section}
        </PillSection>
      ))}
      {onRemove && (
        <Button
          stopPropagation
          onClick={!loading ? onRemove : undefined}
          variant="ghost"
          size="small"
          icon="exit"
          iconSize={grid(2)}
          iconOnly
          className={styles.removeButton}
        />
      )}
    </View>
  );
}

function PillSection({
  children,
  numberOfLines,
  italic,
  strikeThrough,
  className,
  maxWidth,
  textStyle,
}: Pick<Props, 'children' | 'numberOfLines' | 'italic' | 'strikeThrough' | 'textStyle'> & {
  className: (string | false)[];
  maxWidth: CSSProperties['maxWidth'];
}) {
  const isPrimitiveChild = isString(children) || isNumber(children);

  return (
    <View className={className} style={{ maxWidth }}>
      {isPrimitiveChild && (
        <Text
          flex
          size="body1"
          numberOfLines={numberOfLines}
          italic={italic}
          lineThrough={strikeThrough}
          style={textStyle}
          color={italic ? Colors.v2.gray50 : undefined}
        >
          {children}
        </Text>
      )}
      {!isPrimitiveChild && children}
    </View>
  );
}

// Pill's public `style` prop still accepts RN-style objects (several caller chains type it as
// StyleProp<ViewStyle>). Flatten them and expand the RN-only shorthand keys to CSS.
function convertPillStyleToCss(style: StyleProp<ViewStyle>): CSSProperties {
  if (isNil(style)) {
    return {};
  }
  const { marginVertical, marginHorizontal, paddingVertical, paddingHorizontal, ...flattened } =
    StyleSheet.flatten(style);
  return {
    ...(!isNil(marginVertical) && { marginTop: marginVertical, marginBottom: marginVertical }),
    ...(!isNil(marginHorizontal) && {
      marginLeft: marginHorizontal,
      marginRight: marginHorizontal,
    }),
    ...(!isNil(paddingVertical) && { paddingTop: paddingVertical, paddingBottom: paddingVertical }),
    ...(!isNil(paddingHorizontal) && {
      paddingLeft: paddingHorizontal,
      paddingRight: paddingHorizontal,
    }),
    ...flattened,
  } as CSSProperties;
}
